/**
 * Form Lookup Select Component
 *
 * Reusable dropdown for selecting forms when configuring FormContainer child forms.
 * Adapted from flow-studio's LookupSelect with form-specific configuration.
 *
 * Features:
 * - Lazy loads selected form on mount
 * - Lazy loads all forms when dropdown opens
 * - Search/filter within dropdown
 * - 5-minute caching for performance
 * - Error/loading states
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLazyFormLookupData, LookupItem } from '../../hooks/useLazyFormLookupData';
import './FormLookupSelect.css';
import { LoadingIcon, ChevronUpIcon, ChevronDownIcon, CloseIcon } from '@atlas-forms/ui-components-react';

export interface FormLookupSelectProps {
  /**
   * Currently selected form ID
   */
  value: string | number | null;

  /**
   * Callback when value changes
   */
  onChange: (formID: string | number | null) => void;

  /**
   * Field label
   */
  label?: string;

  /**
   * Is field required?
   */
  required?: boolean;

  /**
   * Is field disabled?
   */
  disabled?: boolean;

  /**
   * Show error message
   */
  error?: string;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Maximum items to show in dropdown
   */
  maxItemsInDropdown?: number;

  /**
   * Enable search/filter in dropdown
   */
  searchable?: boolean;

  /**
   * Placeholder text
   */
  placeholder?: string;
}

const FormLookupSelect: React.FC<FormLookupSelectProps> = ({
  value,
  onChange,
  label = 'Form',
  required = false,
  disabled = false,
  error,
  className = '',
  maxItemsInDropdown = 500,
  searchable = true,
  placeholder = 'Select a form...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Use lazy loading hook
  const {
    data,
    selectedItem,
    loading,
    error: hookError,
    hasLoadedAll,
    lazyLoadOnDropdownOpen,
  } = useLazyFormLookupData(value, {
    initialLoad: true, // Load selected form on mount
    cacheExpiresIn: 5 * 60 * 1000, // 5 minutes
  });

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm.trim()) {
      return data;
    }

    const term = searchTerm.toLowerCase();
    return data.filter((item: LookupItem) =>
      (item.name ?? '').toLowerCase().includes(term) ||
      (item.id != null ? String(item.id).includes(term) : false) ||
      (item.formCode ? item.formCode.toLowerCase().includes(term) : false)
    );
  }, [data, searchTerm, searchable]);

  // Limit items shown in dropdown
  const displayedItems = useMemo(() => {
    return filteredData.slice(0, maxItemsInDropdown);
  }, [filteredData, maxItemsInDropdown]);

  // Handle dropdown toggle
  const handleToggleDropdown = async () => {
    if (!isOpen) {
      setIsOpen(true);
      if (!hasLoadedAll) {
        await lazyLoadOnDropdownOpen();
      }
    } else {
      setIsOpen(false);
    }
  };

  // Handle option selection
  const handleSelectOption = (id: string | number) => {
    onChange(id);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };

  // Get display text for selected item
  const displayText = selectedItem
    ? selectedItem.formCode
      ? `${selectedItem.name} — ${selectedItem.formCode}`
      : selectedItem.name
    : placeholder;

  // Combine errors
  const displayError = error || hookError;

  return (
    <div className={`af-form-lookup-select-wrapper ${className}`}>
      {label && (
        <label className="af-form-lookup-select-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <div className="af-form-lookup-select-container" ref={containerRef}>
        {/* Select Button */}
        <button
          className={`af-form-lookup-select-button ${error ? 'input-error' : ''} ${isOpen ? 'open' : ''}`}
          onClick={handleToggleDropdown}
          disabled={disabled}
          type="button"
        >
          <span className="af-form-lookup-select-text">{displayText}</span>
          <span className="af-form-lookup-select-icon">
            {loading ? <LoadingIcon width={12} height={12} /> : isOpen ? <ChevronUpIcon width={12} height={12} /> : <ChevronDownIcon width={12} height={12} />}
          </span>
        </button>

        {/* Clear Button */}
        {value && !disabled && (
          <button
            className="af-form-lookup-clear-button"
            onClick={handleClear}
            type="button"
            title="Clear selection"
          >
            <CloseIcon width={12} height={12} />
          </button>
        )}

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="af-form-lookup-dropdown-menu">
            {/* Search Input */}
            {searchable && (
              <div className="af-form-lookup-search-input-wrapper">
                <input
                  type="text"
                  className="af-form-lookup-search-input"
                  placeholder="Search forms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    className="af-form-lookup-search-clear"
                    onClick={() => setSearchTerm('')}
                    type="button"
                  >
                    <CloseIcon width={12} height={12} />
                  </button>
                )}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="af-form-lookup-menu-message">
                <span><LoadingIcon width={14} height={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Loading forms...</span>
              </div>
            )}

            {/* Error State */}
            {hookError && (
              <div className="af-form-lookup-menu-message af-form-lookup-error">
                <span>Failed to load forms</span>
                <small>{hookError}</small>
              </div>
            )}

            {/* Empty State */}
            {!loading && !hookError && data.length === 0 && (
              <div className="af-form-lookup-menu-message">
                <span>No forms available</span>
              </div>
            )}

            {/* No Search Results */}
            {!loading && !hookError && data.length > 0 && displayedItems.length === 0 && (
              <div className="af-form-lookup-menu-message">
                <span>No forms match "{searchTerm}"</span>
              </div>
            )}

            {/* Options List */}
            {!loading && !hookError && displayedItems.length > 0 && (
              <>
                <div className="af-form-lookup-menu-options">
                  {displayedItems.map((item: LookupItem) => {
                    const isSelected = value != null && value == item.id;
                    return (
                      <button
                        key={item.id}
                        className={`af-form-lookup-menu-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectOption(item.id)}
                        type="button"
                      >
                        <div className="af-form-lookup-option-info">
                          <span className="af-form-lookup-option-name">{item.name}</span>
                          <span className="af-form-lookup-option-code">{item.formCode}</span>
                        </div>
                        {isSelected && (
                          <span className="af-form-lookup-option-checkmark">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Show more indicator */}
                {filteredData.length > maxItemsInDropdown && (
                  <div className="af-form-lookup-menu-more">
                    <small>Showing {displayedItems.length} of {filteredData.length} results</small>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <span className="af-form-lookup-error-text">{displayError}</span>
      )}
    </div>
  );
};

export default FormLookupSelect;
